/**
 * Os reels das parceiras.
 *
 * Na fila, cada vídeo toca sem som enquanto está à vista e pára quando sai:
 * vídeos a correr fora do ecrã gastavam bateria e dados a quem não os vê.
 * Com o movimento reduzido no sistema, nenhum toca sozinho — fica a capa.
 *
 * Carregar num abre-o numa janela, no tamanho maior e com som. O clique é o
 * gesto que o navegador exige para deixar tocar com som.
 */
class ClaroReels extends HTMLElement {
  connectedCallback() {
    this.caixa = this.querySelector('[data-claro-reel-caixa]');
    this.dentro = this.querySelector('[data-claro-reel-dentro]');
    this.titulo = this.querySelector('[data-claro-reel-titulo]');

    this.controlo = new AbortController();
    const { signal } = this.controlo;

    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.observador = new IntersectionObserver((entradas) => this.#vistos(entradas), { threshold: 0.4 });
      for (const video of this.querySelectorAll('[data-claro-reel-v]')) {
        this.observador.observe(video);
      }
    }

    this.addEventListener('click', (evento) => this.#clique(evento), { signal });
    // Fechar tem de calar o vídeo: esvaziar a janela tira-o de lá
    this.caixa?.addEventListener('close', () => this.dentro?.replaceChildren(), { signal });
  }

  disconnectedCallback() {
    this.controlo?.abort();
    this.observador?.disconnect();
  }

  /** @param {IntersectionObserverEntry[]} entradas */
  #vistos(entradas) {
    for (const entrada of entradas) {
      const video = entrada.target;
      if (!(video instanceof HTMLVideoElement)) continue;

      if (entrada.isIntersecting) {
        // Pode recusar (poupança de dados, por exemplo): fica a capa
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  }

  /** @param {MouseEvent} evento */
  #clique(evento) {
    const alvo = evento.target;
    if (!(alvo instanceof Element)) return;

    // Carregar no fundo escuro, fora da janela, também fecha
    if (alvo === this.caixa || alvo.closest('[data-claro-reel-fechar]')) {
      this.caixa?.close();
      return;
    }

    const botao = alvo.closest('[data-claro-reel]');
    if (!(botao instanceof HTMLButtonElement)) return;

    this.#abre(botao.dataset.claroReel ?? '', botao.dataset.titulo ?? '');
  }

  /**
   * @param {string} url
   * @param {string} titulo
   */
  #abre(url, titulo) {
    if (!url || !(this.caixa instanceof HTMLDialogElement) || !this.dentro) return;

    const video = document.createElement('video');
    video.className = 'claro-reel-caixa__v';
    video.src = url;
    video.controls = true;
    video.playsInline = true;

    if (this.titulo) this.titulo.textContent = titulo;
    this.dentro.replaceChildren(video);
    this.caixa.showModal();
    video.play().catch(() => {});
  }
}

if (!customElements.get('claro-reels')) {
  customElements.define('claro-reels', ClaroReels);
}
